import { useEffect, useState, useRef } from "react";
import { CheckIcon } from "@heroicons/react/20/solid";
import { XMarkIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { useSearchParams } from "react-router-dom";
import FAQ from "./FAQ";
import { API_CONFIG, PRICING_PLAN_IDS } from "../config/envConfig";

const frequencies = [
  { value: "monthly", label: "Monthly", priceSuffix: "/month" },
  { value: "annually", label: "Annually", priceSuffix: "/month" },
];

// Beautiful Thin Strip Modal Component
const ThinStripModal = ({ isOpen, onClose, emoji = "✨", message = "Contact sales for custom plans!" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal Strip */}
      <div className="relative animate-[slideDown_0.3s_ease-out] w-full max-w-xl mx-auto">
        <div 
          className="rounded-full sm:rounded-2xl bg-white px-4 py-3 sm:px-6 sm:py-4 shadow-2xl border border-white/20 backdrop-blur-sm flex items-center gap-2 sm:gap-3 transition-all duration-300 hover:scale-105"
          style={{
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* Emoji and Message */}
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-1 min-w-0">
            <span className="text-xl sm:text-2xl flex-shrink-0">{emoji}</span>
            <span className="font-medium text-[#13C296] text-xs sm:text-sm md:text-base leading-tight break-words">{message}</span>
          </div>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-black/10 hover:bg-black/20 active:bg-black/30 transition-all duration-200 hover:scale-110 active:scale-95 group flex-shrink-0 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label="Close"
          >
            <XMarkIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-black group-hover:text-black/80" />
          </button>
        </div>
        
        {/* Subtle glow effect */}
        <div 
          className="absolute inset-0 rounded-full sm:rounded-2xl blur-xl opacity-30 -z-10"
          style={{
            background: 'linear-gradient(135deg, #10b981 0%, #ffffff 100%)'
          }}
        />
      </div>
    </div>
  );
};

// Contact Sales Modal Component (matching EarlyAccessForm style)
const ContactSalesModal = ({ isOpen, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUrl = (url) => {
    if (!url.trim()) return true; // Website is optional
    try {
      if (!url.includes('.')) {
        return false;
      }
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (website.trim() && !validateUrl(website)) {
      setError('Please enter a valid website URL');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_CONFIG.eapRegistrationUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          website: website.trim() || ''
        })
      });

      const responseData = await response.json();
      console.log('API Response:', responseData);

      // Check if response indicates failure
      if (responseData.status === 'failed') {
        setError(responseData.message || 'Something went wrong. Please try again.');
        return;
      }

      // On success, close modal and show success strip
      onSuccess();
      onClose();
      setEmail('');
      setWebsite('');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setWebsite('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-md sm:max-w-xl rounded-2xl sm:rounded-3xl bg-white border border-slate-200 overflow-hidden mx-auto shadow-xl animate-[slideDown_0.3s_ease-out]">
        {/* Header */}
        <div className="px-4 sm:px-6 md:px-8 pt-5 sm:pt-6 md:pt-8 pb-3 sm:pb-4 border-b border-slate-100">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.18em] text-indigo-500/80 mb-2">
                Contact Sales
              </p>
              <h2 className="text-lg sm:text-xl font-medium text-slate-900 leading-snug">
                Get in touch with our team
              </h2>
              <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-slate-600 leading-relaxed">
                We'll reach out to discuss the best plan for your needs.
              </p>
            </div>
            <button
              onClick={handleClose}
              className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-100 hover:bg-slate-200 active:bg-slate-300 transition-all duration-200 hover:scale-110 active:scale-95 group flex-shrink-0 ml-4 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label="Close"
            >
              <XMarkIcon className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600 group-hover:text-slate-800" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="px-4 sm:px-6 md:px-8 pt-3 sm:pt-4 md:pt-5 pb-4 sm:pb-5 md:pb-6 space-y-3 sm:space-y-4 md:space-y-5"
        >
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-slate-800">
              Email address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              placeholder="you@company.com"
              className="w-full px-3 sm:px-3.5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/80 focus:border-transparent transition-all"
              required
              disabled={loading}
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-slate-800">
              Company domain
            </label>
            <input
              type="text"
              value={website}
              onChange={(e) => {
                setWebsite(e.target.value);
                setError('');
              }}
              placeholder="example.com"
              className="w-full px-3 sm:px-3.5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/80 focus:border-transparent transition-all"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="text-xs sm:text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <button
              type="submit"
              disabled={loading}
              className={`w-full sm:w-auto flex items-center justify-center px-5 sm:px-6 py-2.5 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-fuchsia-500 shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 active:scale-[0.98] transition-all whitespace-nowrap touch-manipulation ${
                loading ? 'opacity-60 cursor-not-allowed' : ''
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>

            <p className="text-[10px] sm:text-xs text-slate-500 leading-relaxed text-center sm:text-left">
              By submitting, you agree to be contacted by our sales team.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

const SettingBilling = ({ onOpenSignUpModal }) => {
  const [frequency, setFrequency] = useState(frequencies[0]);
  const [isContactSalesModalOpen, setIsContactSalesModalOpen] = useState(false);
  const [isContactSalesFormOpen, setIsContactSalesFormOpen] = useState(false);
  const [plan, setPlan] = useState("monthly");
  const [activeTab, setActiveTab] = useState("Plans & Billing");
  const tab1Ref = useRef(null);
  const tab2Ref = useRef(null);
  const navRef = useRef(null);
  const [lineStyle, setLineStyle] = useState({ left: '0px', width: '0px' });
  const [lineContainerStyle, setLineContainerStyle] = useState({ left: '0px', width: '0px' });

  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  // Update slider position based on active button
  useEffect(() => {
    const updateSlider = () => {
      const activeButton = frequency.value === 'monthly' ? monthlyButtonRef.current : annualButtonRef.current;
      const container = activeButton?.parentElement;
      
      if (activeButton && container) {
        const containerRect = container.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();
        const left = buttonRect.left - containerRect.left;
        const width = buttonRect.width;
        
        setSliderStyle({
          width: `${width}px`,
          left: `${left}px`,
        });
      }
    };

    updateSlider();
    window.addEventListener('resize', updateSlider);
    return () => window.removeEventListener('resize', updateSlider);
  }, [frequency.value]);

  useEffect(() => {
    if (tabParam === 'credits') {
      setActiveTab("Credits Tracker");
    }
  }, [tabParam]);

  // Tabbing section: Update blue segment position based on active tab and line container
  useEffect(() => {
    const updateLinePosition = () => {
      if (tab1Ref.current && tab2Ref.current && navRef.current) {
        const tab1Rect = tab1Ref.current.getBoundingClientRect();
        const tab2Rect = tab2Ref.current.getBoundingClientRect();
        const navRect = navRef.current.getBoundingClientRect();
        
        // Calculate line container (from first tab start to last tab end)
        const containerLeft = tab1Rect.left - navRect.left;
        const containerRight = tab2Rect.right - navRect.left;
        const containerWidth = containerRight - containerLeft;
        
        setLineContainerStyle({
          left: `${containerLeft}px`,
          width: `${containerWidth}px`
        });

        // Calculate blue segment absolute position
        const activeTabRef = activeTab === "Plans & Billing" ? tab1Ref : tab2Ref;
        if (activeTabRef.current) {
          const activeRect = activeTabRef.current.getBoundingClientRect();
          const segmentLeft = activeRect.left - navRect.left;
          const segmentWidth = activeRect.width;
          
          setLineStyle({
            left: `${segmentLeft}px`,
            width: `${segmentWidth}px`
          });
        }
      }
    };

    // Update on mount and when activeTab changes
    updateLinePosition();
    
    // Update on window resize
    window.addEventListener('resize', updateLinePosition);
    return () => window.removeEventListener('resize', updateLinePosition);
  }, [activeTab]);

  const planFeatures = [
    // Plan 1: Solo
    {
      layout: "solo",
      users: "1",
      creditsPerMonth: "3,000",
      watchlistAccounts: "50",
      geographies: "1",
      accountImport: {
        monthly: "150",
        yearly: "500",
      },
      collaboration: "No",
      addOnUsers: "No",
      keyFeatures: {
        salesAccountIntelligence: true,
        knowledgeProfile: true,
        pipelineManagement: true,
        aiResearchDoc: true,
        customTemplates: true,
        teamCollaboration: false,
        integrations: true,
      },
    },
    // Plan 2: Starter
    {
      layout: "solo",
      users: "2",
      creditsPerMonth: "5,000",
      watchlistAccounts: "100",
      geographies: "3",
      accountImport: {
        monthly: "300",
        yearly: "1,000",
      },
      collaboration: "Yes",
      addOnUsers: "$29/user",
      keyFeatures: {
        salesAccountIntelligence: true,
        knowledgeProfile: true,
        pipelineManagement: true,
        aiResearchDoc: true,
        customTemplates: true,
        teamCollaboration: true,
        integrations: true,
      },
    },
    // Plan 3: Growth
    {
      layout: "solo",
      users: "5",
      creditsPerMonth: "15,000",
      watchlistAccounts: "300",
      geographies: "10",
      accountImport: {
        monthly: "900",
        yearly: "3,000",
      },
      collaboration: "Yes",
      addOnUsers: "$29/user",
      keyFeatures: {
        salesAccountIntelligence: true,
        knowledgeProfile: true,
        pipelineManagement: true,
        aiResearchDoc: true,
        customTemplates: true,
        teamCollaboration: true,
        integrations: true,
      },
    },
    // Plan 4: Pro
    {
      layout: "solo",
      users: "10",
      creditsPerMonth: "35,000",
      watchlistAccounts: "1,000",
      geographies: "Unlimited",
      accountImport: {
        monthly: "3,000",
        yearly: "10,000",
      },
      collaboration: "Yes",
      addOnUsers: "$29/user",
      keyFeatures: {
        salesAccountIntelligence: true,
        knowledgeProfile: true,
        pipelineManagement: true,
        aiResearchDoc: true,
        customTemplates: true,
        teamCollaboration: true,
        integrations: true,
      },
    },
  ];

  // Info tooltips for key features (only items with info data)
  const featureInfo = {
    users: "Add, edit, remove, modify and perform all activities covered in the plan.",
    viewOnlyUsers: "View-only permission to see signals, opportunities, docs, etc., but cannot perform any activity.",
    knowledgeProfile: "Consulting firms' knowledge profile based on the website, documents and past projects.",
    pipelineManagement: "Lead, account and contact management along with AI research for contact discovery.",
    aiResearchDoc: "For custom research and preparing pre-sales documents like proposals, opportunity notes, approach notes, etc.",
    teamCollaboration: "Share signals and insights. Comment and collaborate.",
    integrations: "CRM, Email and Calendar.",
    creditRollovers: "Credits roll over for 1 month on monthly plans, or until the end of your annual plan.",
  };

  const monthlyButtonRef = useRef(null);
  const annualButtonRef = useRef(null);
  const [sliderStyle, setSliderStyle] = useState({ width: 0, left: 0 });

  // Create array with 4 cards with proper pricing data (monthly ≈ annual ÷ 10)
  const displayPlans = () => {
    return [
      {
        id: 'plan-solo',
        pricingPlanId: PRICING_PLAN_IDS.solo,
        pricingPlanIdAnnual: PRICING_PLAN_IDS.soloAnnual,
        plan_name: "Solo",
        price: 49,
        annualPrice: 499,
        subtitle: "per month",
        headline: "Solo",
        buttonText: "Buy now",
        buttonSubtext: null,
        hidecolo: true,
      },
      {
        id: 'plan-starter',
        pricingPlanId: PRICING_PLAN_IDS.starter,
        pricingPlanIdAnnual: PRICING_PLAN_IDS.starterAnnual,
        plan_name: "Starter",
        price: 99,
        annualPrice: 990,
        subtitle: "per month",
        headline: "Small consulting teams",
        buttonText: "Buy now",
        buttonSubtext: null,
      },
      {
        id: 'plan-growth',
        pricingPlanId: PRICING_PLAN_IDS.growth,
        pricingPlanIdAnnual: PRICING_PLAN_IDS.growthAnnual,
        plan_name: "Growth",
        price: 249,
        annualPrice: 2490,
        subtitle: "per month",
        headline: "Growing consulting practices",
        buttonText: "Buy now",
        buttonSubtext: null,
      },
      {
        id: 'plan-pro',
        pricingPlanId: 1,
        plan_name: "Pro",
        price: 449,
        annualPrice: 4490,
        subtitle: "per month",
        headline: "Large consulting team",
        buttonText: "Contact sales",
        buttonSubtext: null,
      },
    ];
  };

  return (
    <>
      <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen overflow-x-hidden">
        {/* Tabbing section */}
        <div className="pt-4 pb-3 px-4 sm:px-6 max-w-7xl mx-auto w-full">
            <nav ref={navRef} className="flex space-x-6 sm:space-x-8" aria-label="Tabs">
              <button
                ref={tab1Ref}
                onClick={() => setActiveTab("Plans & Billing")}
                className={`relative pb-3 text-sm sm:text-base font-semibold transition-all duration-200 whitespace-nowrap ${
                  activeTab === "Plans & Billing"
                    ? 'text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Plans & Billing
              </button>
              <button
                ref={tab2Ref}
                onClick={() => setActiveTab("Credits Tracker")}
                className={`relative pb-3 text-sm sm:text-base font-semibold transition-all duration-200 whitespace-nowrap ${
                  activeTab === "Credits Tracker"
                    ? 'text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Credits Tracker
              </button>
            </nav>
            {/* Continuous horizontal line below tabs with blue segment under active tab */}
            <div className="relative h-[3px] mt-[-3px]">
              {/* Light gray/blue background line spanning from first tab to last tab */}
              <div 
                className="absolute h-full bg-gray-200 rounded-full"
                style={{
                  left: lineContainerStyle.left,
                  width: lineContainerStyle.width
                }}
              ></div>
              {/* Blue segment aligned exactly to active tab text start */}
              <div 
                className="absolute h-full bg-blue-600 rounded-full transition-all duration-300 shadow-sm"
                style={{
                  left: lineStyle.left,
                  width: lineStyle.width
                }}
              ></div>
            </div>
          </div>
          {/* Tabbing section */}
        {activeTab === "Plans & Billing" && <div className="pb-8 w-full">
          <div className="mx-auto max-w-6xl text-center mb-4 px-4 sm:px-6 w-full">
            <h1 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 mb-2">
              Plans and <span className="text-blue-600">Pricing</span>
            </h1>
            <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto px-4">
              Choose the perfect plan for your consulting firm. All plans include our core features with flexible add-ons.
            </p>
          </div>
          <div className="my-4 flex justify-center px-4 sm:px-6 w-full">
            <div
              className="relative inline-flex items-center p-1 rounded-full bg-gray-100 border border-gray-200 shadow-sm w-full max-w-xs"
            >
              {/* Sliding background indicator */}
              <div
                className="absolute h-[calc(100%-4px)] bg-white rounded-full shadow-md transition-all duration-300 ease-in-out border border-gray-100"
                style={{
                  width: sliderStyle.width || 'auto',
                  left: sliderStyle.left || '2px',
                  top: '2px',
                }}
              />
              <button
                ref={monthlyButtonRef}
                className={`relative z-10 flex-1 px-4 sm:px-6 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 outline-none focus-visible:outline-none focus-within:outline-none focus:outline-none whitespace-nowrap ${frequency.value === 'monthly' ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                onClick={() => {setFrequency(frequencies[0]); setPlan("monthly")}}
              >
                Monthly
              </button>
              <button
                ref={annualButtonRef}
                className={`relative z-10 flex-1 px-4 sm:px-6 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 outline-none focus-visible:outline-none focus-within:outline-none focus:outline-none flex items-center justify-center gap-1 sm:gap-2 whitespace-nowrap ${frequency.value === 'annually' ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                onClick={() => {setFrequency(frequencies[1]); setPlan("yearly")}}
              >
                Annually
              </button>
            </div>
          </div>
          {/* Cards Grid Container */}
          <div className="py-4 px-2 sm:px-3 w-full overflow-visible">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-3 md:gap-2 max-w-7xl mx-auto w-full overflow-visible">
              {displayPlans()
                .map((tier, featureIdx) => ({ tier, featureIdx }))
                .filter(({ tier }) => !tier.hidecolo)
                .map(({ tier, featureIdx }) => (
                <div
                  key={tier.id}
                  className="flex flex-col h-full relative"
                >
                  <div
                    className={`
                      rounded-2xl border-2 flex flex-col h-full min-w-0 relative overflow-visible
                      transition-all duration-300 hover:-translate-y-1
                      ${tier.plan_name === "Growth"
                        ? "border-yellow-400 bg-white"
                        : "border-gray-200 bg-white"
                      }
                    `}
                    style={{ zIndex: 1 }}
                  >
                    {/* Popular Badge */}
                    {tier.plan_name === "Growth" && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                        <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full shadow-lg">
                          Most Popular
                        </span>
                      </div>
                    )}
                    <div className="p-2.5 sm:p-3 pb-2.5 min-w-0 w-full">
                      {/* Plan Name and Badge */}
                      <div className="mb-2.5">
                        <div className="flex items-start justify-between mb-1.5 gap-2">
                          <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{tier.plan_name}</h3>
                        </div>
                        {tier.headline && (
                          <p className="text-xs text-gray-500 leading-relaxed break-words">
                            {tier.headline}
                          </p>
                        )}
                      </div>
                      
                      {/* Price and subtitle */}
                      <div className="mb-3 pb-3 border-b border-gray-200">
                        <div className="flex items-baseline gap-1 sm:gap-2 mb-1 flex-wrap">
                          {tier.price === null ? (
                            <span className="text-xl sm:text-2xl font-bold text-gray-900">Custom Pricing</span>
                          ) : (
                            <>
                              {plan === "yearly" ? (
                                <>
                                  <span className="text-2xl sm:text-3xl font-bold text-gray-900">${tier.annualPrice}</span>
                                </>
                              ) : (
                                <span className="text-2xl sm:text-3xl font-bold text-gray-900">${tier.price}</span>
                              )}
                            </>
                          )}
                          {tier.price !== null && (
                             <span className="text-gray-500 text-sm sm:text-base font-medium">{plan === "yearly" ? "/yr" : "/mo"}</span>
                          )}
                        </div>
                        {tier.subtitle ? (
                          <div className="flex items-center gap-1 text-xs text-gray-600 flex-wrap">
                            <span className="break-words">{plan === "yearly" ? "billed annually" : tier.subtitle}</span>
                          </div>
                        )
                        : (
                          <div className="mb-6">
                          </div>
                        )
                        }
                      </div>
                      
                      {/* Plan Details */}
                      <div className="space-y-1.5 mb-3 min-w-0">
                        {planFeatures[featureIdx]?.layout === "solo" ? (
                          <>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <span className="text-xs font-medium text-gray-600">Users</span>
                                {featureIdx === 0 && (
                                  <div className="relative group z-50">
                                    <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-blue-500 transition-colors" aria-hidden />
                                    <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal border border-gray-700 z-50">
                                      {featureInfo.users}
                                      <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
                                    </div>
                                  </div>
                                )}
                              </div>
                              <span className="text-xs font-semibold text-gray-900 text-right truncate">{planFeatures[featureIdx].users}</span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Credits/month</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">{planFeatures[featureIdx].creditsPerMonth}</span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Watchlist accounts</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">{planFeatures[featureIdx].watchlistAccounts}</span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Geographies</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">{planFeatures[featureIdx].geographies}</span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Account import</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">{planFeatures[featureIdx].accountImport[plan]}</span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Collaboration</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">{planFeatures[featureIdx].collaboration}</span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Add-on users</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">{planFeatures[featureIdx].addOnUsers}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Users</span>
                              <span className="text-xs font-semibold text-gray-900 text-right truncate">{planFeatures[featureIdx]?.users}</span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Accounts (Watchlist)</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">
                                {planFeatures[featureIdx]?.accounts}
                              </span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">People (Watchlist)</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">
                                {planFeatures[featureIdx]?.people}
                              </span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Geographies</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">
                                {planFeatures[featureIdx]?.geographies}
                              </span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Industries</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">
                                {planFeatures[featureIdx]?.industries}
                              </span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Triggers</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">
                                {planFeatures[featureIdx]?.triggers}
                              </span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Personas</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">
                                {planFeatures[featureIdx]?.personas}
                              </span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Credits</span>
                              <span className="text-xs font-semibold text-gray-900 text-right truncate">
                                {planFeatures[featureIdx]?.credits}
                              </span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Storage</span>
                              <span className="text-xs font-semibold text-gray-900 text-right truncate">
                                {planFeatures[featureIdx]?.storage}
                              </span>
                            </div>
                            <div className="flex items-center justify-between py-1.5 gap-2">
                              <span className="text-xs font-medium text-gray-600 flex-shrink-0">Add-on user</span>
                              <span className="text-xs font-semibold text-gray-900 text-right break-words">{planFeatures[featureIdx]?.addOnMember || 'Nil'}</span>
                            </div>
                          </>
                        )}
                      </div>

                      {/* CTA Button */}
                      <div className="mb-3">
                        <button
                          onClick={() => {
                            if (tier.plan_name === "Pro") {
                              setIsContactSalesFormOpen(true);
                              return;
                            }
                            const planId = plan === 'yearly' ? tier.pricingPlanIdAnnual : tier.pricingPlanId;
                            onOpenSignUpModal?.({ mode: 'pricing-register', planId });
                          }}
                          className="w-full rounded-lg py-2 text-xs sm:text-sm font-semibold transition-colors duration-200 bg-gray-900 text-white hover:bg-gray-700"
                        >
                          {tier.buttonText}
                        </button>
                        {tier.buttonSubtext && (
                          <p className="text-[10px] text-gray-500 text-center mt-1.5">
                            {tier.buttonSubtext}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Features List */}
                    <div className="px-2.5 sm:px-3 pb-3 pt-2.5 bg-gray-50 rounded-b-2xl min-w-0 w-full overflow-visible hidden">
                      {/* KEY FEATURES Heading */}
                      <div className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wide text-left">
                        Key Features
                      </div>

                      <div className="space-y-1.5 min-w-0 overflow-visible">
                        {/* Sales & Account Intelligence */}
                        <div className="flex items-start gap-2 min-w-0">
                          {planFeatures[featureIdx]?.keyFeatures?.salesAccountIntelligence ? (
                            <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XMarkIcon className="text-red-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          )}
                          <span className={`text-xs ${planFeatures[featureIdx]?.keyFeatures?.salesAccountIntelligence ? 'text-gray-700' : 'text-gray-400'} break-words min-w-0`}>
                            Sales & Account Intelligence
                          </span>
                        </div>

                        {/* Knowledge Profile */}
                        <div className="flex items-start gap-2 min-w-0">
                          {planFeatures[featureIdx]?.keyFeatures?.knowledgeProfile ? (
                            <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XMarkIcon className="text-red-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex items-center gap-1.5 flex-1 min-w-0">
                            <span className={`text-xs ${planFeatures[featureIdx]?.keyFeatures?.knowledgeProfile ? 'text-gray-700' : 'text-gray-400'} break-words min-w-0`}>
                              Knowledge Profile
                            </span>
                            {featureIdx === 0 && (
                              <div className="relative group z-50">
                                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-blue-500 transition-colors" />
                                <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal border border-gray-700 z-50">
                                  {featureInfo.knowledgeProfile}
                                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Pipeline Management */}
                        <div className="flex items-start gap-2 min-w-0">
                          {planFeatures[featureIdx]?.keyFeatures?.pipelineManagement ? (
                            <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XMarkIcon className="text-red-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex items-center gap-1.5 flex-1 min-w-0">
                            <span className={`text-xs ${planFeatures[featureIdx]?.keyFeatures?.pipelineManagement ? 'text-gray-700' : 'text-gray-400'} break-words min-w-0`}>
                              Pipeline Management
                            </span>
                            {featureIdx === 0 && (
                              <div className="relative group z-50">
                                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-blue-500 transition-colors" />
                                <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal border border-gray-700 z-50">
                                  {featureInfo.pipelineManagement}
                                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* AI Research & AI Doc */}
                        <div className="flex items-start gap-2 min-w-0">
                          {planFeatures[featureIdx]?.keyFeatures?.aiResearchDoc ? (
                            <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XMarkIcon className="text-red-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex items-center gap-1.5 flex-1 min-w-0">
                            <span className={`text-xs ${planFeatures[featureIdx]?.keyFeatures?.aiResearchDoc ? 'text-gray-700' : 'text-gray-400'} break-words min-w-0`}>
                              AI Research & AI Doc
                            </span>
                            {featureIdx === 0 && (
                              <div className="relative group z-50">
                                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-blue-500 transition-colors" />
                                <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal border border-gray-700 z-50">
                                  {featureInfo.aiResearchDoc}
                                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Custom Templates */}
                        <div className="flex items-start gap-2 min-w-0">
                          {planFeatures[featureIdx]?.keyFeatures?.customTemplates ? (
                            <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XMarkIcon className="text-red-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          )}
                          <span className={`text-xs ${planFeatures[featureIdx]?.keyFeatures?.customTemplates ? 'text-gray-700' : 'text-gray-400'} break-words min-w-0`}>
                            Custom Templates for Research & Docs
                          </span>
                        </div>

                        {/* Team Collaboration */}
                        <div className="flex items-start gap-2 min-w-0">
                          {planFeatures[featureIdx]?.keyFeatures?.teamCollaboration ? (
                            <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XMarkIcon className="text-red-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex items-center gap-1.5 flex-1 min-w-0">
                            <span className={`text-xs ${planFeatures[featureIdx]?.keyFeatures?.teamCollaboration ? 'text-gray-700' : 'text-gray-700'} break-words min-w-0`}>
                              Team Collaboration
                            </span>
                            {featureIdx === 0 && (
                              <div className="relative group z-50">
                                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-blue-500 transition-colors" />
                                <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal border border-gray-700 z-50">
                                  {featureInfo.teamCollaboration}
                                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Integrations */}
                        <div className="flex items-start gap-2 min-w-0">
                          {planFeatures[featureIdx]?.keyFeatures?.integrations ? (
                            <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XMarkIcon className="text-red-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex items-center gap-1.5 flex-1 min-w-0">
                            <span className={`text-xs ${planFeatures[featureIdx]?.keyFeatures?.integrations ? 'text-gray-700' : 'text-gray-700'} break-words min-w-0`}>
                              Integrations
                            </span>
                            {featureIdx === 0 && (
                              <div className="relative group z-50">
                                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-blue-500 transition-colors" />
                                <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal border border-gray-700 z-50">
                                  {featureInfo.integrations}
                                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="max-w-7xl mx-auto mt-8 sm:mt-10 w-full">
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5 sm:p-6">
                <div className="text-xs font-bold text-gray-900 mb-3 uppercase tracking-wide text-left">
                  Key Features
                </div>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs text-gray-700 break-words min-w-0">Sales & Account Intelligence</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs text-gray-700 break-words min-w-0">Knowledge Profile</span>
                      <div className="relative group z-50">
                        <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-blue-500 transition-colors" aria-hidden />
                        <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal border border-gray-700 z-50">
                          {featureInfo.knowledgeProfile}
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs text-gray-700 break-words min-w-0">Pipeline Management</span>
                      <div className="relative group z-50">
                        <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-blue-500 transition-colors" aria-hidden />
                        <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal border border-gray-700 z-50">
                          {featureInfo.pipelineManagement}
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs text-gray-700 break-words min-w-0">AI Research & AI Doc</span>
                      <div className="relative group z-50">
                        <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-blue-500 transition-colors" aria-hidden />
                        <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal border border-gray-700 z-50">
                          {featureInfo.aiResearchDoc}
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs text-gray-700 break-words min-w-0">Custom Templates for Research & Docs</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs text-gray-700 break-words min-w-0">Team Collaboration</span>
                      <div className="relative group z-50">
                        <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-blue-500 transition-colors" aria-hidden />
                        <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal border border-gray-700 z-50">
                          {featureInfo.teamCollaboration}
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs text-gray-700 break-words min-w-0">Integrations</span>
                      <div className="relative group z-50">
                        <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-blue-500 transition-colors" aria-hidden />
                        <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal border border-gray-700 z-50">
                          {featureInfo.integrations}
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckIcon className="text-green-500 w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs text-gray-700 break-words min-w-0">Credit rollovers</span>
                      <div className="relative group z-50">
                        <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-blue-500 transition-colors" aria-hidden />
                        <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal border border-gray-700 z-50">
                          {featureInfo.creditRollovers}
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      </div>
      <div>
        <FAQ />
      </div>

      {/* Contact Sales Form Modal */}
      <ContactSalesModal 
        isOpen={isContactSalesFormOpen}
        onClose={() => setIsContactSalesFormOpen(false)}
        onSuccess={() => setIsContactSalesModalOpen(true)}
      />

      {/* Beautiful Thin Strip Modal */}
      <ThinStripModal 
        isOpen={isContactSalesModalOpen}
        onClose={() => setIsContactSalesModalOpen(false)}
        emoji="✓"
        message="Thank you for your request. Our team has received it and will contact you shortly."
      />
      
    </>
  );
};

export default SettingBilling;